import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";
import {
  useAgentChat,
  type ChatContext,
  type ChatMessage,
} from "@/hooks/useAgentChat";

import {
  AgentHero,
  AgentMsg,
  AgentThread,
  UserMsg,
} from "@/components/atomic-crm/shared/agent";

import { MessageContent } from "./chat/blocks/MessageContent";

// Sprint 9 — Vue agent embarquée sur /dev, /companies, /appointments (mode D2).
// Utilise les composants partagés shared/agent/ (AgentHero + AgentThread + UserMsg + AgentMsg).
// Conversations scopées par context.page via chat_conversations.

type ConversationSummary = {
  id: string;
  title: string;
  updated_at: string;
};

interface EmbeddedAgentViewProps {
  pageId: "dev" | "companies" | "appointments" | "contact" | "company";
  pageLabel: string;
  /** Contexte additionnel envoyé à l'agent (entity_type, entity_id, filters...). */
  context?: Omit<ChatContext, "page">;
  /** Prompts suggérés pré-remplis pour cette page. */
  suggestedPrompts?: string[];
  className?: string;
  /**
   * Clé de scoping additionnelle pour isoler les conversations à l'intérieur
   * d'un même pageId. Ex: pageId="contact" + scopeKey="245" → conversations
   * propres à la fiche contact #245, pas mélangées avec les autres contacts.
   */
  scopeKey?: string;
}

const PAGE_ICON = {
  dev: "💻",
  companies: "🏢",
  appointments: "📅",
  contact: "👤",
  company: "🏢",
} as const;

export function EmbeddedAgentView({
  pageId,
  pageLabel,
  context,
  suggestedPrompts = [],
  className,
  scopeKey,
}: EmbeddedAgentViewProps) {
  const chat = useAgentChat();
  const [input, setInput] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const ctx: ChatContext = { page: pageId, ...(context ?? {}) };

  const matchesScope = useCallback(
    (raw: unknown) => {
      const ctxObj = raw as {
        page?: string;
        entity_id?: string | number;
      } | null;
      if (ctxObj?.page !== pageId) return false;
      if (scopeKey && String(ctxObj?.entity_id ?? "") !== scopeKey)
        return false;
      return true;
    },
    [pageId, scopeKey],
  );

  const reloadConversations = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const supabase = getSupabaseClient();
      const { data } = await supabase
        .from("chat_conversations")
        .select("id, title, updated_at, context")
        .is("archived_at", null)
        .order("updated_at", { ascending: false })
        .limit(20);
      setConversations(
        (data ?? [])
          .filter((c: { context: unknown }) => matchesScope(c.context))
          .map((c: { id: string; title: string; updated_at: string }) => ({
            id: c.id,
            title: c.title || "Sans titre",
            updated_at: c.updated_at,
          })),
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [matchesScope]);

  useEffect(() => {
    let cancelled = false;
    chat.newConversation();
    (async () => {
      const supabase = getSupabaseClient();
      const { data } = await supabase
        .from("chat_conversations")
        .select("id, context")
        .is("archived_at", null)
        .order("updated_at", { ascending: false })
        .limit(20);
      if (cancelled) return;
      const last = (data ?? []).find((c: { context: unknown }) =>
        matchesScope(c.context),
      );
      if (last) {
        await chat.loadConversation(last.id);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, scopeKey]);

  const handleSubmit = () => {
    const text = input.trim();
    if (!text || chat.status === "streaming") return;
    setInput("");
    void chat.send(text, ctx);
  };

  const handleSuggested = (prompt: string) => {
    if (chat.status === "streaming") return;
    void chat.send(prompt, ctx);
  };

  const handleNewConversation = () => {
    chat.newConversation();
    setHistoryOpen(false);
    inputRef.current?.focus();
  };

  const handleOpenHistory = async () => {
    setHistoryOpen((open) => !open);
    if (!historyOpen) {
      await reloadConversations();
    }
  };

  const handlePromptFollowUp = (text: string) => {
    void chat.send(text, ctx);
  };

  const hasMessages = chat.messages.length > 0;
  const isStreaming = chat.status === "streaming";

  const historySlot = historyLoading ? (
    <div className="text-[12px] text-muted-foreground flex items-center gap-2">
      <Loader2 className="size-3 animate-spin" />
      Chargement…
    </div>
  ) : conversations.length === 0 ? (
    <div className="text-[12px] text-muted-foreground">
      Aucune conversation pour cette page.
    </div>
  ) : (
    <ul className="flex flex-col gap-0.5">
      {conversations.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => {
              void chat.loadConversation(c.id);
              setHistoryOpen(false);
            }}
            className={cn(
              "w-full text-left px-2 py-1.5 rounded text-[12.5px] hover:bg-fabrik-green-soft",
              chat.conversationId === c.id && "bg-fabrik-green-soft",
            )}
          >
            <div className="truncate font-medium">{c.title}</div>
            <div className="text-[10.5px] text-muted-foreground">
              {new Date(c.updated_at).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <AgentHero
        label={pageLabel.toLowerCase()}
        description={
          <>
            Filtre, analyse, génère des résumés et des actions sur{" "}
            {PAGE_ICON[pageId]} {pageLabel.toLowerCase()}. Les conversations
            sont enregistrées pour améliorer l'agent.
          </>
        }
        input={input}
        onInputChange={setInput}
        onSubmit={handleSubmit}
        onStop={() => chat.stop()}
        isStreaming={isStreaming}
        suggestedPrompts={suggestedPrompts}
        onSuggestedClick={handleSuggested}
        onOpenHistory={handleOpenHistory}
        isHistoryOpen={historyOpen}
        onNewConversation={handleNewConversation}
        historySlot={historySlot}
        showSuggestions={!hasMessages}
      />

      {hasMessages && (
        <AgentThread scrollKey={chat.messages.length}>
          {chat.messages.map((m) => (
            <ChatBubble
              key={m.id}
              message={m}
              onPrompt={handlePromptFollowUp}
            />
          ))}
        </AgentThread>
      )}

      {chat.error && (
        <div className="rounded-md border border-fabrik-red bg-fabrik-red-soft px-3 py-2 text-[12.5px] text-fabrik-red">
          Erreur : {chat.error}
        </div>
      )}
    </div>
  );
}

function ChatBubble({
  message,
  onPrompt,
}: {
  message: ChatMessage;
  onPrompt?: (text: string) => void;
}) {
  if (message.role === "user") {
    return (
      <UserMsg>
        <MessageContent content={message.content} onPrompt={onPrompt} />
      </UserMsg>
    );
  }
  return (
    <AgentMsg pending={message.pending && !message.content}>
      <MessageContent content={message.content} onPrompt={onPrompt} />
    </AgentMsg>
  );
}
